import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { DesktopAndMobile, SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Combobox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { StyledNoTouchPurchaseSelectTrigger } from "app/components/no-touch-purchase-modal/summary-modal/selection/StyledNoTouchPurchaseSummaryModalSelection";
import type { INoTouchAddon } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { getSingleDiscountValue } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { discount, NO_TOUCH_PURCHASE_TRANSLATIONS } from "app/components/no-touch-purchase-modal/translations";
import { ADDON_TYPE_NAME_DICT, type AddonTypeName } from "app/contexts/AddonStateContext/const";
import { getCurrency } from "app/pages/digital-suite/home-page/components/AfterTrialPopup/utils";
import { isMonthlyPaymentPeriod } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

const getSelectItem = (item: INoTouchAddon, addonType: AddonTypeName) => {
    if (addonType === ADDON_TYPE_NAME_DICT.CountryFilterDynamic) {
        return (
            <Flex gap="8px">
                <SWReactCountryIcons
                    size={"xs"}
                    countryCode={item.countryFilterData?.countryId ?? 0}
                />
                {item.displayName}
                <DesktopAndMobile
                    size={"xs"}
                    color={colorsPalettes.carbon[200]}
                />
            </Flex>
        );
    }
    return item.displayName;
};

interface INoTouchPurchaseSummaryModalSelectionProps {
    currency?: string;
    addonType: AddonTypeName;
    selection?: INoTouchAddon[];
    selectedAddon: INoTouchAddon;
    onSelectedAddonChange: (newAddon: INoTouchAddon) => void;
}

export const NoTouchPurchaseSummaryModalSelection = ({
    currency = "USD",
    addonType,
    selection = [],
    selectedAddon,
    onSelectedAddonChange,
}: INoTouchPurchaseSummaryModalSelectionProps) => {
    const isMonthly = isMonthlyPaymentPeriod(swSettings);
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const periodText = translate(
        isMonthly
            ? NO_TOUCH_PURCHASE_TRANSLATIONS.details.selectionPeriodMonth
            : NO_TOUCH_PURCHASE_TRANSLATIONS.details.selectionPeriodYear,
    );

    const searchPlaceholderKey = NO_TOUCH_PURCHASE_TRANSLATIONS.summary[addonType]?.searchPlaceholder;
    const hasSearch = Boolean(searchPlaceholderKey);

    const filteredSelection = useMemo(() => {
        if (!hasSearch || !searchValue) {
            return selection;
        }
        const lowerCaseSearch = searchValue.toLowerCase();
        return selection.filter((item) => item.displayName.toLowerCase().includes(lowerCaseSearch));
    }, [hasSearch, searchValue, selection]);

    const handleSelectAddon = (addon: INoTouchAddon) => {
        onSelectedAddonChange(addon);
        setIsOpen(false);
        setSearchValue("");
    };

    const purchasedIndex = filteredSelection.findIndex((item) => item.isPurchased);

    const discountValue = getSingleDiscountValue(selectedAddon);
    const currencySign = getCurrency(currency).sign;

    return (
        <Flex
            flexDirection="column"
            gap="24px"
        >
            <Text fontVariant="body2">{translate(NO_TOUCH_PURCHASE_TRANSLATIONS.summary[addonType].description)}</Text>

            <Flex
                flexDirection="column"
                gap="8px"
            >
                <Flex justifyContent="space-between">
                    <Text fontVariant="caption">
                        {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.summary[addonType].caption)}
                    </Text>
                    {discountValue ? (
                        <Badge
                            type="custom"
                            color={colorsPalettes.carbon[0]}
                            bgColor={colorsPalettes.green["400_P"]}
                        >
                            {translate(discount.addonsPercentOff, {
                                percent: `${discountValue * 100}`,
                            })}
                        </Badge>
                    ) : null}
                </Flex>
                <Combobox
                    data-automation="no-touch-purchase-selection-combobox"
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    placement="bottom-start"
                    width="430px"
                >
                    <Combobox.Trigger>
                        <StyledNoTouchPurchaseSelectTrigger data-automation="no-touch-purchase-selection-trigger">
                            <Flex
                                width="100%"
                                padding="0 12px 0 16px"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                {getSelectItem(selectedAddon, addonType)}
                                <Flex>
                                    {currencySign}
                                    <Flex>
                                        {selectedAddon.price.toLocaleString()}
                                        {" / "}
                                        {periodText}
                                    </Flex>
                                </Flex>
                            </Flex>
                        </StyledNoTouchPurchaseSelectTrigger>
                    </Combobox.Trigger>

                    {hasSearch ? (
                        <Combobox.SearchInput
                            value={searchValue}
                            onChange={setSearchValue}
                            placeholder={translate(searchPlaceholderKey ?? "")}
                        />
                    ) : null}

                    <Combobox.Content>
                        {filteredSelection.map((item, index) => {
                            const isDisabled = index <= purchasedIndex;
                            return (
                                <Combobox.Option
                                    key={item.id}
                                    id={item.id}
                                    textValue={item.displayName}
                                    isSelected={item.id === selectedAddon.id}
                                    isDisabled={isDisabled}
                                    aria-label={`${item.displayName} ${currency} ${item.price}`}
                                    onClick={() => {
                                        if (!isDisabled) {
                                            handleSelectAddon(item);
                                        }
                                    }}
                                >
                                    <Flex
                                        justifyContent="space-between"
                                        width="100%"
                                    >
                                        <Flex>{getSelectItem(item, addonType)}</Flex>
                                        <Flex gap="8px">
                                            {item.isPurchased ? (
                                                <Badge
                                                    type="custom"
                                                    color={colorsPalettes.carbon[0]}
                                                    bgColor={colorsPalettes.carbon[100]}
                                                >
                                                    {translate("addons.badge.purchased")}
                                                </Badge>
                                            ) : null}
                                            <Flex>
                                                {currencySign}
                                                {item.price.toLocaleString()}
                                                {" / "}
                                                {periodText}
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Combobox.Option>
                            );
                        })}
                    </Combobox.Content>
                </Combobox>
            </Flex>
        </Flex>
    );
};
